/**
 * Enterprise Connector management — typed REST surface.
 *
 * Wraps the BE router at
 * ``ando-agent/src/api/v1/endpoints/enterprise_connectors.py`` (prefix
 * ``/enterprise/connectors``). Exposes the 11 endpoints needed by the
 * post-create operator surfaces (SchemaViewer, DiscoveryDialog,
 * EditConnectorDialog, BusinessContextEditor, ChatExamplesManager).
 *
 * V1 source-of-truth (verbatim signatures):
 *   ``ando-frontend/app/api/enterprise.ts`` — lines:
 *     - 181  updateConnector
 *     - 388  testConnection
 *     - 400  discoverSchema
 *     - 408  getProfilingStatus
 *     - 417  getSchema
 *     - 423  updateSchemaDescription  (table + columns batched in ONE call)
 *     - 441  listExamples
 *     - 449  createExample
 *     - 459  deleteExample
 *     - 472  updateBusinessContext
 *     - 482  regenerateBusinessContext
 *
 * The wire contracts mirror the V1 Pydantic shapes exactly so the BE
 * doesn't need a second adapter. Only the apiClient base + JSON encoding
 * change vs V1 (apiFetch → apiClient).
 */

import { apiClient } from "../ApiClient.js";

const BASE = "/enterprise/connectors";

// ---------------------------------------------------------------------------
// Wire types — mirror BE Pydantic models verbatim
// ---------------------------------------------------------------------------

/** Italian VAT / provider id / DB host metadata exposed on the list+detail. */
export interface EnterpriseConnectorSummary {
  readonly id: string;
  readonly name: string;
  readonly connector_type: string;
  readonly status: string;
  readonly schema_count: number;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly vat_number?: string | null;
  readonly provider_id?: string | null;
}

export interface DetectedRelationship {
  readonly from_table: string;
  readonly from_column: string;
  readonly to_table: string;
  readonly to_column: string;
}

export interface EnterpriseConnectorDetail extends EnterpriseConnectorSummary {
  readonly db_dialect: string | null;
  readonly schema_filter: readonly string[] | null;
  readonly rate_limit_per_minute: number | null;
  readonly max_query_timeout_seconds: number | null;
  readonly max_rows_per_query: number | null;
  readonly business_context?: string;
  /**
   * True when a person wrote/edited the context. Profiling never regenerates
   * or overwrites it; ``regenerateEnterpriseBusinessContext`` needs
   * ``force: true`` (the lock is released afterwards).
   */
  readonly business_context_user_edited?: boolean;
  readonly detected_relationships?: readonly DetectedRelationship[];
  readonly connection_host?: string | null;
  readonly connection_port?: number | null;
  readonly connection_database?: string | null;
  readonly connection_username?: string | null;
}

export interface UpdateConnectorRequest {
  readonly name?: string;
  readonly credentials?: Readonly<Record<string, unknown>>;
  readonly config?: Readonly<Record<string, unknown>>;
}

export interface ConnectionTestResponse {
  readonly success: boolean;
  readonly latency_ms: number;
  readonly error: string | null;
  readonly server_version: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

export interface DiscoverResponse {
  /** "started" — BE runs profiling in the background. */
  readonly status: string;
}

export interface ColumnSchemaResponse {
  readonly name: string;
  readonly data_type: string;
  readonly nullable: boolean;
  readonly is_primary_key: boolean;
  readonly foreign_key: string | null;
  readonly description: string;
  readonly sample_values: readonly unknown[];
  readonly business_synonyms: readonly string[];
  readonly aggregation_hint: string | null;
  readonly cardinality: string | null;
  readonly is_identifier: boolean;
  readonly is_categorical: boolean;
  /** Coded value -> meaning. ERP enums store opaque integers, so the raw
   *  sample value alone is unreadable; this maps it to a human label. */
  readonly value_descriptions?: Readonly<Record<string, string>>;
}

export interface TableSchemaResponse {
  readonly name: string;
  readonly description: string;
  readonly columns: readonly ColumnSchemaResponse[];
  readonly record_count: number | null;
  readonly relationships: readonly string[];
  readonly domain: string | null;
}

export type ProfilingStatus = "pending" | "in_progress" | "complete" | "no_schemas";

export interface ProfilingStatusResponse {
  readonly total_tables: number;
  readonly profiled_tables: number;
  readonly status: ProfilingStatus;
  readonly is_discovering: boolean;
  /** True while the LLM profiling background task is running. */
  readonly is_profiling: boolean;
  readonly business_context?: string;
  /** Hand-curated context: a re-discovery keeps it (see ``DiscoveryDialog``). */
  readonly business_context_user_edited?: boolean;
  readonly discovery_error?: string;
}

export interface SchemaResponse {
  readonly tables: readonly TableSchemaResponse[];
  readonly discovered_at: string | null;
  readonly connector_type: string;
}

export interface SchemaUpdateRequest {
  /** Free-text description for the table itself. Required. */
  readonly description: string;
  /** Per-column description overrides, keyed by column name. */
  readonly column_descriptions?: Readonly<Record<string, string>>;
}

export interface SchemaUpdateResponse {
  readonly updated: boolean;
  readonly object_name: string;
}

export interface ExampleResponse {
  readonly id: string;
  readonly question: string;
  readonly sql_query: string;
  readonly source: string;
  readonly is_validated: boolean;
  readonly used_count: number;
  readonly created_at: string | null;
}

export interface CreateExampleRequest {
  readonly question: string;
  readonly sql_query: string;
  readonly source?: string;
}

export interface BusinessContextUpdateBody {
  /** New full text of the business context. */
  readonly business_context: string;
}

export interface RegenerateBusinessContextResponse {
  readonly business_context: string;
  /** Always ``false`` after a regeneration: the text is AI-authored again. */
  readonly business_context_user_edited?: boolean;
}

export interface RegenerateBusinessContextOptions {
  /**
   * Required when the current context is user-edited — the BE answers 409
   * otherwise. Callers must confirm with the person first: the hand-written
   * text is replaced, not merged.
   */
  readonly force?: boolean;
}

// ---------------------------------------------------------------------------
// List + delete (May 2026 — power the integrations hub connection roll-up)
// ---------------------------------------------------------------------------

/**
 * GET ``/enterprise/connectors`` — flat list of every enterprise
 * connector for the authenticated user, across all ``connector_type``
 * values. The BE returns a bare array (NOT a ``{connectors: [...]}``
 * wrapper); we accept both shapes defensively for forward-compat.
 *
 * Consumers (the integrations hub) filter by ``connector_type`` to
 * present them under the matching capability tile (database / erp /
 * spreadsheet) — these are NOT in the white-label
 * ``SUPPORTED_CAPABILITIES`` whitelist so they can't be listed via the
 * unified ``/connectors/{capability}/connections`` route.
 */
export async function listEnterpriseConnectors(): Promise<readonly EnterpriseConnectorSummary[]> {
  const res = await apiClient.get<unknown>(BASE);
  if (Array.isArray(res)) return res as EnterpriseConnectorSummary[];
  return (res as { connectors?: readonly EnterpriseConnectorSummary[] })?.connectors ?? [];
}

/**
 * DELETE ``/enterprise/connectors/{conn_id}`` — removes the connector +
 * cascades its schema / examples. 204 No Content on success.
 */
export async function disconnectEnterpriseConnector(connId: string): Promise<void> {
  await apiClient.delete<void>(`${BASE}/${encodeURIComponent(connId)}`);
}

/** Body for ``POST /enterprise/connectors`` — mirrors BE ``ConnectorCreateRequest``. */
export interface ConnectorCreateRequest {
  readonly name: string;
  /** ``ConnectorType`` value (postgresql / mysql / sqlserver / oracle / …). */
  readonly connector_type: string;
  /** Connection secrets (host/port/user/password, file_path, dsn, …). */
  readonly credentials: Readonly<Record<string, unknown>>;
  /** Optional tuning (schema_filter, rate_limit_per_minute, …) — set later via Edit. */
  readonly config?: Readonly<Record<string, unknown>>;
}

/**
 * POST ``/enterprise/connectors`` — create a database / ERP connector.
 * Spreadsheet uses the dedicated multipart ``/import-spreadsheet`` route
 * instead (handled by its own dialog), NOT this one.
 */
export async function createEnterpriseConnector(
  body: ConnectorCreateRequest,
): Promise<EnterpriseConnectorSummary> {
  return apiClient.post<EnterpriseConnectorSummary>(BASE, body);
}

// ---------------------------------------------------------------------------
// CRUD — update only (create+list+delete live in spreadsheetApi / wizards)
// ---------------------------------------------------------------------------

/**
 * PUT ``/enterprise/connectors/{conn_id}`` — update name / credentials / config.
 *
 * Mirrors V1 ``enterprise.ts::updateConnector`` (line 181). Empty
 * credentials object on the wire is the documented signal for "keep the
 * existing secret" — callers MUST strip blank password fields before
 * forwarding.
 */
export async function updateEnterpriseConnector(
  connId: string,
  body: UpdateConnectorRequest,
): Promise<EnterpriseConnectorDetail> {
  return apiClient.put<EnterpriseConnectorDetail>(`${BASE}/${encodeURIComponent(connId)}`, body);
}

// ---------------------------------------------------------------------------
// Connection test
// ---------------------------------------------------------------------------

/**
 * POST ``/enterprise/connectors/{conn_id}/test``.
 *
 * Mirrors V1 ``enterprise.ts::testConnection`` (line 388). The BE returns
 * a 200 with ``success: false`` for credential / network errors — never
 * raises — so the FE branches on ``success`` rather than try/catching.
 */
/**
 * Optional shape forwarded to the BE.
 *
 * - **omitted / undefined credentials** → BE tests the SAVED state via
 *   the registry (legacy create-time path, no regression).
 * - **credentials object provided** → BE builds a TEMPORARY connector
 *   from those creds, runs ``check_connection()``, and discards it
 *   without persisting. Used by EditConnectorDialog to verify draft
 *   form values before "Salva" — SOTA admin-UX (Vault, Retool,
 *   Linear DB editor).
 *
 * Stays ``Record<string, unknown>`` because the field set is wizard-
 * specific (host/port/database/user for DB, account_id/api_key for
 * SaaS) and the wizard already enforces shape at submit time.
 */
export interface TestEnterpriseConnectionVars {
  readonly credentials?: Readonly<Record<string, unknown>>;
}

export async function testEnterpriseConnection(
  connId: string,
  vars?: TestEnterpriseConnectionVars,
): Promise<ConnectionTestResponse> {
  // Body shape mirrors BE ``ConnectorTestRequest`` (June 10, 2026).
  // Send ``{}`` when nothing supplied so the BE treats it as the
  // legacy saved-state probe (Optional body is None there).
  const body = vars?.credentials !== undefined ? { credentials: vars.credentials } : {};
  return apiClient.post<ConnectionTestResponse>(`${BASE}/${encodeURIComponent(connId)}/test`, body);
}

// ---------------------------------------------------------------------------
// Schema discovery (async)
// ---------------------------------------------------------------------------

/**
 * POST ``/enterprise/connectors/{conn_id}/discover`` — kick off background
 * profiling. Returns ``{status: "started"}``; the FE polls
 * ``getProfilingStatus`` for progress.
 *
 * Mirrors V1 ``enterprise.ts::discoverSchema`` (line 400). BE responds
 * 202 Accepted; apiClient treats that as a normal success.
 */
export async function discoverEnterpriseSchema(connId: string): Promise<DiscoverResponse> {
  return apiClient.post<DiscoverResponse>(`${BASE}/${encodeURIComponent(connId)}/discover`, {});
}

export interface DiscoveryCostEstimate {
  catalog_status: "current" | "unavailable";
  total_tables: number | null;
  total_columns: number | null;
  tables_to_profile: number | null;
  columns_to_profile: number | null;
  unchanged_tables: number | null;
  blocked_tables: number | null;
  baseline_review_tables: number | null;
  mode: "delta" | "first_run" | "unknown";
  provider_cost_usd: { low: number; high: number } | null;
  customer_cost_usd: number | null;
  requires_confirmation: boolean;
  downstream_cost_included: boolean;
  assumptions: string[];
}

/** Metadata-only preflight. Does not launch profiling or paid model calls. */
export async function estimateEnterpriseDiscovery(connId: string): Promise<DiscoveryCostEstimate> {
  return apiClient.get<DiscoveryCostEstimate>(
    `${BASE}/${encodeURIComponent(connId)}/discovery-estimate`,
  );
}

/**
 * GET ``/enterprise/connectors/{conn_id}/profiling-status`` — polled while
 * discovery / profiling is running. ``status === "complete"`` is the
 * terminal happy state; ``"no_schemas"`` means no tables were discovered
 * (likely a permission issue).
 *
 * Mirrors V1 ``enterprise.ts::getProfilingStatus`` (line 408).
 */
export async function getEnterpriseProfilingStatus(
  connId: string,
): Promise<ProfilingStatusResponse> {
  return apiClient.get<ProfilingStatusResponse>(
    `${BASE}/${encodeURIComponent(connId)}/profiling-status`,
  );
}

/**
 * GET ``/enterprise/connectors/{conn_id}/schema`` — full table list with
 * column metadata + AI-generated descriptions.
 *
 * Mirrors V1 ``enterprise.ts::getSchema`` (line 417).
 */
export async function getEnterpriseSchema(connId: string): Promise<SchemaResponse> {
  return apiClient.get<SchemaResponse>(`${BASE}/${encodeURIComponent(connId)}/schema`);
}

/**
 * PUT ``/enterprise/connectors/{conn_id}/schema/{object_name}``.
 *
 * V1 has a SINGLE endpoint that accepts BOTH the table-level description
 * AND a ``column_descriptions`` record map in one body. We mirror that
 * exactly — splitting it FE-side would force the BE to handle two writes
 * for a single user save action.
 *
 * Mirrors V1 ``enterprise.ts::updateSchemaDescription`` (line 423).
 */
export async function updateEnterpriseSchemaDescription(
  connId: string,
  objectName: string,
  body: SchemaUpdateRequest,
): Promise<SchemaUpdateResponse> {
  return apiClient.put<SchemaUpdateResponse>(
    `${BASE}/${encodeURIComponent(connId)}/schema/${encodeURIComponent(objectName)}`,
    body,
  );
}

// ---------------------------------------------------------------------------
// Data preview — sample values / rows (helps write better descriptions)
// ---------------------------------------------------------------------------

export interface SampleColumnResponse {
  readonly object_name: string;
  readonly column: string;
  /** Up to `limit` DISTINCT live values (JSON-coerced). Empty = no data / failed. */
  readonly values: readonly unknown[];
}

export interface SampleRowsResponse {
  readonly object_name: string;
  readonly columns: readonly string[];
  readonly rows: readonly Readonly<Record<string, unknown>>[];
}

/**
 * GET ``/enterprise/connectors/{conn_id}/sample-column`` — up to ``limit``
 * DISTINCT LIVE values for a column, so the user can see what's actually in a
 * field while writing its description. Best-effort (empty on failure).
 */
export async function getEnterpriseSampleColumn(
  connId: string,
  objectName: string,
  column: string,
  limit = 20,
): Promise<SampleColumnResponse> {
  const qs = new URLSearchParams({
    object_name: objectName,
    column,
    limit: String(limit),
  });
  return apiClient.get<SampleColumnResponse>(
    `${BASE}/${encodeURIComponent(connId)}/sample-column?${qs.toString()}`,
  );
}

/**
 * GET ``/enterprise/connectors/{conn_id}/sample-rows`` — up to ``limit`` sample
 * ROWS for a table (live), so the user can peek at the data shape before
 * annotating it. Routed BE-side through the SELECT-only validator.
 */
export async function getEnterpriseSampleRows(
  connId: string,
  objectName: string,
  limit = 20,
): Promise<SampleRowsResponse> {
  const qs = new URLSearchParams({
    object_name: objectName,
    limit: String(limit),
  });
  return apiClient.get<SampleRowsResponse>(
    `${BASE}/${encodeURIComponent(connId)}/sample-rows?${qs.toString()}`,
  );
}

// ---------------------------------------------------------------------------
// Few-shot SQL examples
// ---------------------------------------------------------------------------

/**
 * GET ``/enterprise/connectors/{conn_id}/examples``.
 *
 * Mirrors V1 ``enterprise.ts::listExamples`` (line 441).
 */
export async function listEnterpriseExamples(connId: string): Promise<readonly ExampleResponse[]> {
  return apiClient.get<readonly ExampleResponse[]>(
    `${BASE}/${encodeURIComponent(connId)}/examples`,
  );
}

/**
 * POST ``/enterprise/connectors/{conn_id}/examples``.
 *
 * Mirrors V1 ``enterprise.ts::createExample`` (line 449). V1 takes two
 * positional args (``question``, ``sql``) — we keep the BE body shape
 * (``CreateExampleRequest``) so the caller composes the request explicitly.
 */
export async function createEnterpriseExample(
  connId: string,
  body: CreateExampleRequest,
): Promise<ExampleResponse> {
  return apiClient.post<ExampleResponse>(`${BASE}/${encodeURIComponent(connId)}/examples`, body);
}

/**
 * DELETE ``/enterprise/connectors/{conn_id}/examples/{example_id}``.
 *
 * Mirrors V1 ``enterprise.ts::deleteExample`` (line 459). 204 No Content
 * on success — apiClient resolves to void.
 */
export async function deleteEnterpriseExample(connId: string, exampleId: string): Promise<void> {
  await apiClient.delete<void>(
    `${BASE}/${encodeURIComponent(connId)}/examples/${encodeURIComponent(exampleId)}`,
  );
}

// ---------------------------------------------------------------------------
// Business context
// ---------------------------------------------------------------------------

/**
 * PUT ``/enterprise/connectors/{conn_id}/business-context``.
 *
 * Mirrors V1 ``enterprise.ts::updateBusinessContext`` (line 472). 204 No
 * Content on success.
 */
export async function updateEnterpriseBusinessContext(
  connId: string,
  businessContext: string,
): Promise<void> {
  const body: BusinessContextUpdateBody = { business_context: businessContext };
  await apiClient.put<void>(`${BASE}/${encodeURIComponent(connId)}/business-context`, body);
}

export interface CollectValuesBody {
  /** Restrict to these object names (default: whole catalogue). */
  readonly object_names?: readonly string[];
  /** Skip objects that already carry distinct values / ranges (default true). */
  readonly only_unsampled?: boolean;
  /** Skip objects with more rows than this; ``null`` disables the cap. */
  readonly max_rows?: number | null;
}

/**
 * POST ``/enterprise/connectors/{conn_id}/collect-values`` — 202 Accepted.
 *
 * Values-only sampling (DISTINCT values / ranges) WITHOUT LLM profiling. The
 * companion of a hand-curated schema: after a vendor data dictionary has been
 * imported, this fills the code values the text-to-SQL prompt relies on while
 * leaving descriptions and the business context untouched. Runs in the
 * background; idempotent with ``only_unsampled``.
 */
export async function collectEnterpriseValues(
  connId: string,
  body: CollectValuesBody = {},
): Promise<void> {
  await apiClient.post<{ status: "started" }>(
    `${BASE}/${encodeURIComponent(connId)}/collect-values`,
    body,
  );
}

/**
 * POST ``/enterprise/connectors/{conn_id}/regenerate-context``.
 *
 * Triggers the LLM to re-summarise the schema into a fresh
 * ``business_context``. Wall-clock 10-30s — callers should disable the
 * trigger button while ``isPending``.
 *
 * Mirrors V1 ``enterprise.ts::regenerateBusinessContext`` (line 482).
 */
export async function regenerateEnterpriseBusinessContext(
  connId: string,
  { force = false }: RegenerateBusinessContextOptions = {},
): Promise<RegenerateBusinessContextResponse> {
  const query = force ? "?force=true" : "";
  return apiClient.post<RegenerateBusinessContextResponse>(
    `${BASE}/${encodeURIComponent(connId)}/regenerate-context${query}`,
    {},
  );
}

// ── Vendor data dictionary import ──────────────────────────────────────────

/**
 * Coverage preview returned by ``import-dictionary`` with ``dry_run=true``:
 * how much of the discovered catalogue the uploaded Excel dictionary covers,
 * before anything is written.
 */
export interface ImportDictionaryPreview {
  readonly dictionary_tables: number;
  readonly dictionary_columns: number;
  readonly discovered_objects: number;
  /** Company prefix used to match dictionary names to DB objects (detected or given). */
  readonly prefix: string | null;
  readonly matched_tables: number;
  readonly documented_columns: number;
  readonly total_columns_in_matched: number;
  /** DB objects with no dictionary entry — first 50 only. */
  readonly unmatched_objects: readonly string[];
  /** Matched tables ranked by number of columns still without a description. */
  readonly undocumented_top: readonly { readonly object_name: string; readonly missing: number }[];
}

export interface ImportDictionaryOptions {
  /** Company prefix (``FOM_``); omitted ⇒ the BE detects it. */
  readonly prefix?: string;
  /** Sample code values (no AI) after the import — default true. */
  readonly collectValues?: boolean;
  /** Run the AI profiler only on objects the dictionary does not cover — default true. */
  readonly profileUncovered?: boolean;
}

export interface ImportDictionaryStarted {
  readonly status: "started";
}

/**
 * POST ``/enterprise/connectors/{conn_id}/import-dictionary`` (multipart).
 *
 * ``dryRun: true`` answers 200 with an {@link ImportDictionaryPreview};
 * ``dryRun: false`` answers 202 ``{status:"started"}`` and the pipeline
 * (import → values → AI on uncovered objects) runs in the background —
 * progress is visible through ``getEnterpriseProfilingStatus``.
 *
 * Multipart via ``postMultipart`` (NOT ``post``) so ``fetch`` sets the
 * ``boundary`` FastAPI's ``UploadFile`` parser needs. Booleans travel as
 * the literal strings ``"true"`` / ``"false"``.
 */
export async function importEnterpriseDictionary(
  connId: string,
  file: File,
  opts: ImportDictionaryOptions & { readonly dryRun: true },
): Promise<ImportDictionaryPreview>;
export async function importEnterpriseDictionary(
  connId: string,
  file: File,
  opts: ImportDictionaryOptions & { readonly dryRun: false },
): Promise<ImportDictionaryStarted>;
export async function importEnterpriseDictionary(
  connId: string,
  file: File,
  {
    dryRun,
    prefix,
    collectValues = true,
    profileUncovered = true,
  }: ImportDictionaryOptions & {
    readonly dryRun: boolean;
  },
): Promise<ImportDictionaryPreview | ImportDictionaryStarted> {
  const form = new FormData();
  form.append("file", file);
  const trimmedPrefix = prefix?.trim();
  if (trimmedPrefix) form.append("prefix", trimmedPrefix);
  form.append("dry_run", dryRun ? "true" : "false");
  form.append("collect_values", collectValues ? "true" : "false");
  form.append("profile_uncovered", profileUncovered ? "true" : "false");
  return apiClient.postMultipart<ImportDictionaryPreview | ImportDictionaryStarted>(
    `${BASE}/${encodeURIComponent(connId)}/import-dictionary`,
    form,
  );
}
